// src/components/PlaceDetails.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../axiosConfig';
import '../styles/PlaceDetails.css';
import { useTranslation } from 'react-i18next';

/* ===== Países (para detetar prefixo do telefone) ===== */
const countryPhoneInfo = [
  { code: 'pt', label: 'Portugal', value: '351' },
  { code: 'es', label: 'Spain', value: '34' },
  { code: 'fr', label: 'France', value: '33' },
  { code: 'de', label: 'Germany', value: '49' },
  { code: 'gb', label: 'United Kingdom', value: '44' },
  { code: 'it', label: 'Italy', value: '39' },
  { code: 'ch', label: 'Switzerland', value: '41' },
  { code: 'be', label: 'Belgium', value: '32' },
  { code: 'nl', label: 'Netherlands', value: '31' },
  { code: 'se', label: 'Sweden', value: '46' },
  { code: 'fi', label: 'Finland', value: '358' },
  { code: 'no', label: 'Norway', value: '47' },
  { code: 'dk', label: 'Denmark', value: '45' },
  { code: 'pl', label: 'Poland', value: '48' },
  { code: 'ee', label: 'Estonia', value: '372' },
  { code: 'cz', label: 'Czech Republic', value: '420' },
  { code: 'hr', label: 'Croatia', value: '385' },
  { code: 'hu', label: 'Hungary', value: '36' },
  { code: 'at', label: 'Austria', value: '43' },
];

// usamos estes códigos como chaves de tradução (Mon..Sun)
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

/* ===== Normalização Working Hours (aceita string/null/parcial) ===== */
function normalizeWorkingHours(wh) {
  if (!wh) return { Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [] };
  let obj = wh;
  if (typeof wh === 'string') {
    try { obj = JSON.parse(wh); } catch { obj = {}; }
  }
  const base = { Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [] };
  return { ...base, ...obj };
}

/* ===== País a partir do número de telefone ===== */
function getCountryByPhoneNumber(phoneNumber) {
  if (!phoneNumber) return null;
  let normalized = phoneNumber.replace(/[\s\-]/g, "");
  if (!normalized.startsWith("+")) normalized = "+" + normalized;
  const ordered = [...countryPhoneInfo].sort((a, b) => b.value.length - a.value.length);
  for (const country of ordered) {
    if (normalized.startsWith("+" + country.value)) return country;
  }
  return null;
}

/* ===== Código da bandeira a partir do nome do país ===== */
function getFlagCode(countryName) {
  if (!countryName) return 'pt';
  const normalized = countryName
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  const countryCodes = {
    austria: 'at',
    belgium: 'be',
    croatia: 'hr',
    'czech republic': 'cz',
    denmark: 'dk',
    estonia: 'ee',
    finland: 'fi',
    france: 'fr',
    germany: 'de',
    hungary: 'hu',
    italy: 'it',
    netherlands: 'nl',
    norway: 'no',
    poland: 'pl',
    portugal: 'pt',
    spain: 'es',
    sweden: 'se',
    switzerland: 'ch',
    'united kingdom': 'gb',
    uk: 'gb',
    'great britain': 'gb',
  };

  return countryCodes[normalized] || 'pt';
}

function PlaceDetails() {
  const { t, ready } = useTranslation('place_details');

  const { placeId } = useParams();
  const [place, setPlace] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlaceDetails = async () => {
      try {
        const { data } = await axios.get(`/places/${placeId}`);
        setPlace(data);
      } catch (error) {
        setErrorMessage(t('errors.load_failed'));
      }
    };
    fetchPlaceDetails();
  }, [placeId, t]);

  const handleEdit = () => navigate(`/places/${placeId}/edit`, { state: { prefill: place } });
  const handleBack = () => navigate('/places');

  if (!place && !errorMessage) {
    return <p className="place-details-loading">{t('loading')}</p>;
  }

  const workingHours = normalizeWorkingHours(place?.workingHours);

  // ——— Preparação do bloco de telefone (flag + prefixo + número) ———
  const phoneView = (() => {
    const country =
      getCountryByPhoneNumber(place?.contactPhone) ||
      { code: getFlagCode(place?.country), label: place?.country, value: '' };

    let displayNumber = place?.contactPhone || '';
    let prefix = '';

    if (country.value) {
      if (displayNumber?.startsWith('+' + country.value)) {
        prefix = '+' + country.value;
        displayNumber = displayNumber.replace('+' + country.value, '');
      } else {
        prefix = '+' + country.value;
      }
    }

    return (
      <div className="place-details-phone-view">
        <img
          src={`https://flagcdn.com/w40/${country.code}.png`}
          alt={country.label}
          className="place-details-flag"
          onError={(e) => (e.currentTarget.style.display = 'none')}
        />
        {prefix && <span className="place-details-phone-prefix">({prefix})</span>}
        <span className="place-details-phone-number">{displayNumber?.trim() || '—'}</span>
      </div>
    );
  })();

  // evita “flash” de keys caso o namespace ainda esteja a carregar
  if (!ready) return null;

  return (
    <div className="place-details-container">
      <div className="place-details-back-arrow" onClick={handleBack}>
        ← {t('back')}
      </div>

      <div className="place-details-content">
        <h1 className="place-details-title">{t('title')}</h1>

        {errorMessage ? (
          <p className="place-details-error-message">{errorMessage}</p>
        ) : (
          <form className="place-details-form">
            {/* Place Name & Contact Phone */}
            <div className="place-details-row">
              <div className="place-details-input-group">
                <label className="place-details-label">{t('fields.place_name')}</label>
                <div className="place-details-input">{place?.placeName || '—'}</div>
              </div>

              <div className="place-details-input-group">
                <label className="place-details-label">{t('fields.contact_phone')}</label>
                {phoneView}
              </div>
            </div>

            {/* Address */}
            <div className="place-details-input-group">
              <label className="place-details-label">{t('fields.address')}</label>
              <div className="place-details-input">{place?.address || '—'}</div>
            </div>

            {/* ZIP + City + Country */}
            <div className="place-details-row place-details-row--3">
              <div className="place-details-input-group">
                <label className="place-details-label">{t('fields.zip_code')}</label>
                <div className="place-details-input">{place?.postalCode || '—'}</div>
              </div>

              <div className="place-details-input-group">
                <label className="place-details-label">{t('fields.city')}</label>
                <div className="place-details-input">{place?.city || '—'}</div>
              </div>

              <div className="place-details-input-group">
                <label className="place-details-label">{t('fields.country')}</label>
                <div className="place-details-country-view">
                  <img
                    src={`https://flagcdn.com/w40/${getFlagCode(place?.country)}.png`}
                    alt={place?.country}
                    className="place-details-flag"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                  <span className="place-details-country-name">{place?.country || '—'}</span>
                </div>
              </div>
            </div>

            {/* GMaps + Coordinates */}
            <div className="place-details-row">
              <div className="place-details-input-group">
                <label className="place-details-label">{t('fields.gmaps_link')}</label>
                {place?.gMapsLink ? (
                  <a
                    href={place.gMapsLink}
                    target="_blank"
                    rel="noreferrer"
                    className="place-details-input place-details-link"
                    title={place.gMapsLink}
                  >
                    {place.gMapsLink}
                  </a>
                ) : (
                  <div className="place-details-input">—</div>
                )}
              </div>

              <div className="place-details-input-group">
                <label className="place-details-label">{t('fields.coordinates')}</label>
                <div className="place-details-input">{place?.coordinates || '—'}</div>
              </div>
            </div>

            {/* Working Hours */}
            <div className="place-details-workinghours">
              <div className="place-details-wh-header">
                <label className="place-details-label">{t('working_hours.title')}</label>
              </div>

              <div className="place-details-wh-grid">
                {DAYS.map((day) => {
                  const intervals = workingHours[day] || [];
                  return (
                    <div key={day} className="place-details-wh-day">
                      <div className="place-details-wh-day-name">{t(`days.${day}`)}</div>

                      {intervals.length === 0 ? (
                        <div className="place-details-wh-closed">{t('working_hours.closed')}</div>
                      ) : (
                        intervals.map((pair, idx) => (
                          <div key={idx} className="place-details-wh-row">
                            <span className="place-details-wh-time">{pair[0] || '—'}</span>
                            <span className="place-details-wh-sep">–</span>
                            <span className="place-details-wh-time">{pair[1] || '—'}</span>
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="place-details-button-row">
              <button type="button" className="place-details-edit-button" onClick={handleEdit}>
                {t('actions.edit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default PlaceDetails;
